import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import { adminAPI } from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";
import CustomContainer from "../../components/common/CustomContainer";

const Settings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    siteName: "E-Library",
    allowRegistration: true,
    maxPdfFileSize: 50,
    maxVideoFileSize: 500,
  });
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await adminAPI.getSettings();
        setSettings({
          siteName: response.data.siteName || "E-Library",
          allowRegistration: response.data.allowRegistration === "true",
          maxPdfFileSize: parseInt(response.data.maxPdfFileSize) || 50,
          maxVideoFileSize: parseInt(response.data.maxVideoFileSize) || 500,
        });

        // Update document title when settings are loaded
        document.title = response.data.siteName || "E-Library";
      } catch (error) {
        console.error("Error fetching settings:", error);
        showSnackbar(t("settings.messages.loadFailed"), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [showSnackbar]);

  // Update document title when siteName changes
  useEffect(() => {
    document.title = settings.siteName;
  }, [settings.siteName]);

  const handleSettingChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveSettings = async () => {
    try {
      // Validate settings before saving
      if (settings.maxPdfFileSize < 1 || settings.maxPdfFileSize > 100) {
        showSnackbar(t("settings.messages.pdfSizeError"), "error");
        return;
      }

      if (settings.maxVideoFileSize < 1 || settings.maxVideoFileSize > 1000) {
        showSnackbar(t("settings.messages.videoSizeError"), "error");
        return;
      }

      // Format settings for backend
      const formattedSettings = {
        siteName: settings.siteName,
        allowRegistration: settings.allowRegistration.toString(),
        maxPdfFileSize: settings.maxPdfFileSize.toString(),
        maxVideoFileSize: settings.maxVideoFileSize.toString(),
      };

      await adminAPI.updateSettings(formattedSettings);
      showSnackbar(t("settings.messages.saveSuccess"), "success");
    } catch (error) {
      console.error("Error saving settings:", error);
      showSnackbar(t("settings.messages.saveFailed"), "error");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CustomContainer>
      <Typography variant="h4" gutterBottom>
        {t("settings.system.title")}
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("settings.system.general")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("settings.system.siteName")}
                value={settings.siteName}
                onChange={(e) =>
                  handleSettingChange("siteName", e.target.value)
                }
                helperText={t("settings.system.siteNameHelper")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.allowRegistration}
                    onChange={(e) =>
                      handleSettingChange("allowRegistration", e.target.checked)
                    }
                  />
                }
                label={t("settings.system.registration")}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                {t("settings.system.fileUpload.title")}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t("settings.system.fileUpload.maxPdfSize")}
                value={settings.maxPdfFileSize}
                onChange={(e) =>
                  handleSettingChange(
                    "maxPdfFileSize",
                    parseInt(e.target.value)
                  )
                }
                helperText={t("settings.system.fileUpload.maxPdfHelper")}
                inputProps={{
                  min: 1,
                  max: 100,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label={t("settings.system.fileUpload.maxVideoSize")}
                value={settings.maxVideoFileSize}
                onChange={(e) =>
                  handleSettingChange(
                    "maxVideoFileSize",
                    parseInt(e.target.value)
                  )
                }
                helperText={t("settings.system.fileUpload.maxVideoHelper")}
                inputProps={{
                  min: 1,
                  max: 1000,
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
        >
          {t("settings.actions.saveChanges")}
        </Button>
      </Box>
    </CustomContainer>
  );
};

export default Settings;
